/**
 * Handler code for the site wide image recognition search form.
 * This code is site wide so we can't rely on Angular or any other library code.
 */

var ImageDownsampler = require('./image_downsampler.js');
var scriptData = require('./script_data.js');

var _lastTarget = null;

var BROWSE_FILES_LINK_ID = 'drop-file-here__link';
var CLOSE_BUTTON_CLASS = 'visual-search-drop-zone__close';
var DRAG_AND_DROP_FILE_INPUT_ID = 'visual-search-file-input-drag-and-drop';
var DRAG_AND_DROP_FORM_ID = 'visual-search-drag-and-drop-form';
var DRAG_ZONE_ID = 'drop-file-here__upload';
var DROP_ZONE_CLASS = '.visual-search-drop-zone';
var ERROR_TEXT_CLASS = 'visual-search-error__text';
var ERROR_DETAILS_CLASS = 'visual-search-error__details';
var UPLOAD_ANOTHER_FILE_INPUT_ID = 'upload-another-file-input';
var SEARCH_BY_IMAGE_LINK_ID = 'search-by-image-link';
var TRY_VISUAL_SEARCH_LINK_ID = 'try-a-visual-search';
var VISUAL_SEARCH_FILE_INPUT_SELECTOR = '.visual-search-file-input';
var VISUAL_SEARCH_UPLOAD_FORM_SELECTOR = '#visual-search-upload-form';

var ALLOWED_FILE_TYPES = ['image/gif', 'image/jpeg', 'image/png', 'image/webp'];
var MAX_FILE_BYTES = 5242880;

// Use ready state DONE code from the XMLHttpRequest object if it's available.
var READY_STATE_DONE = XMLHttpRequest.DONE || 4; // eslint-disable-line no-magic-numbers
var OK_STATUS = 200;

// Translations will loaded during initialization.
var i18n;

export function init() {
  // If the document itself has loaded, we can initialize the event handlers, etc.
  if (document.readyState != 'loading') {
    _initWhenReady();
  // If the document is still in a loading state, initialize it once it has loaded.
  } else {
    document.addEventListener('DOMContentLoaded', _initWhenReady);
  }
}

/**
 * Initialize the page listeners and setup the page to handle the use dragging and
 * dropping images.
 */
function _initWhenReady() {
  i18n = scriptData.getData('/visual_search/i18n');

  if (!_getUploadForm()) {
    return;
  }

  _getImageFileInput(_getUploadForm()).
    addEventListener('change', _handleFileInputChange);

  window.addEventListener('dragenter', _handleWindowDragEnter);
  window.addEventListener('dragleave', _handleWindowDragLeave);
  window.addEventListener('dragover', _handleWindowDragOver);
  window.addEventListener('drop', _handleWindowDrop);

  window.addEventListener('af.search-bar.visual-search', function() {
    _getDragAndDropFileInput().addEventListener('change', _handleFileInputChange);
    _handleFileUploadLinkClick();
  });
  _addLinkEventListeners([_getBrowseFilesLink(), _getSearchByImageLink(), _getTryVisualSearchLink()]);

  _initDragAndDropForm();

  // This is called after _initDragAndDropForm() to add the listener to the correct button on the Discover page
  _getCloseButton().addEventListener('click', _handleCloseButtonClick);

  if (_getUploadAnotherFileInput()) {
    _getUploadAnotherFileInput().addEventListener('change', _handleFileInputChange);
  }
}

/*
 * It is possible to start a visual search by clicking a link.
 * Doing so should open a file upload dialog, which requires a listener on the link.
 * If any of these links are present, we need also a listener on the file input
 * that displays the loading screen when a file is uploaded.
 *
 * @private
 * @param {Array} links - An array of HTMLElements (or null) consisting every link on
 *                        the site that would need these listeners.
 */
function _addLinkEventListeners(links) {
  // If an item in links is null, it wasn't found on the page
  var linksOnPage = links.filter(function(e) {
    return e;
  });

  linksOnPage.forEach(function(link) {
    link.addEventListener('click', _handleFileUploadLinkClick);
  });

  if (linksOnPage.length > 0) {
    const input = _getDragZoneFileInput();
    if (input) {
      input.addEventListener('change', _handleFileInputChange);
    }
  }
}

/**
 * Returns the link that opens the dialog to browse for files on your computer
 *
 * @private
 * @returns {Element}
 */
function _getBrowseFilesLink() {
  return document.getElementById(BROWSE_FILES_LINK_ID);
}

/**
 * Returns the button that, if clicked, will close the drag and drop modal and cancel the request
 *
 * @private
 * @returns {Element}
 */
function _getCloseButton() {
  return _getDragAndDropUploadForm().getElementsByClassName(CLOSE_BUTTON_CLASS)[0];
}

/**
 * Returns the file input for the drag and drop element.
 *
 * @private
 * @returns {Element}
 */
function _getDragAndDropFileInput() {
  return document.getElementById(DRAG_AND_DROP_FILE_INPUT_ID);
}

/**
 * Returns the upload form for the drag and drop element.
 *
 * @private
 * @returns {Element}
 */
function _getDragAndDropUploadForm() {
  _toggleDragAndDropUploadFormStyle();
  return document.getElementById(DRAG_AND_DROP_FORM_ID);
}

/**
 * Returns the visual search form action.
 *
 * @private
 * @returns {String}
 */
function _getUploadPath() {
  var path = document.getElementById(DRAG_AND_DROP_FORM_ID).action;
  return path + '.json';
}

/**
 * Returns the file input inside the drag zone specified by DRAG_ZONE_ID
 *
 * @private
 * @returns {Element}
 */
function _getDragZoneFileInput() {
  const dragZone = document.getElementById(DRAG_ZONE_ID);
  if (!dragZone) {
    return;
  }

  return dragZone.querySelector(VISUAL_SEARCH_FILE_INPUT_SELECTOR);
}

/**
 * Returns the image drop zone.
 *
 * @private
 * @returns {Element}
 */
function _getDropZone() {
  return _getDragAndDropUploadForm().querySelector(DROP_ZONE_CLASS);
}

/**
 * Returns the element containing error message text.
 *
 * @private
 * @returns {Element}
 */
function _getErrorText() {
  return document.getElementsByClassName(ERROR_TEXT_CLASS)[0];
}

/**
 * Returns the element containing error message details.
 *
 * @private
 * @returns {Element}
 */
function _getErrorDetails() {
  return document.getElementsByClassName(ERROR_DETAILS_CLASS)[0];
}

/**
 * Returns the input element that will handle uploading the image file to
 * recognize.
 *
 * @private
 * @param {Element} parent
 * @returns {Element}
 */
function _getImageFileInput(parent) {
  return parent.querySelector(VISUAL_SEARCH_FILE_INPUT_SELECTOR);
}

function _getTryVisualSearchLink() {
  return document.getElementById(TRY_VISUAL_SEARCH_LINK_ID);
}

function _getSearchByImageLink() {
  return document.getElementById(SEARCH_BY_IMAGE_LINK_ID);
}

/**
 * Returns the input element inside the "Upload another file" button on the results page
 *
 * @private
 * @returns {Element}
 */
function _getUploadAnotherFileInput() {
  return document.getElementById(UPLOAD_ANOTHER_FILE_INPUT_ID);
}

/**
 * Returns the search form that will submit the actual search.
 *
 * @private
 * @returns {Element}
 */
function _getUploadForm() {
  return document.querySelector(VISUAL_SEARCH_UPLOAD_FORM_SELECTOR);
}

/**
 * Hides the drag and drop modal and clears error text so it isn't displayed on the next search
 *
 * @listens Event
 * @private
 */
function _handleCloseButtonClick() {
  _hideDragAndDropModal();
}

/**
 * Handles change events on the image file input form. Checks the file selected through
 * the dialog opened in _handleBrowseFilesLinkClick and submits the drag and drop form
 * if it is valid.
 *
 * If the input change is in the search bar, this disables the search input, changes
 * the value to file name, and shows the image search button.
 *
 * @listens Event
 * @private
 * @param {Event} e
 */
function _handleFileInputChange(e) {
  _metric('typekit.click.visual-search.browse-select-image');
  _processAndUploadImage(_getImageFileInput(e.target.form).files[0]);
}

/**
 * Opens a dialog to browse files when a file upload link is clicked
 *
 * @listens Event
 * @private
 */
function _handleFileUploadLinkClick() {
  _getImageFileInput(_getUploadForm()).click();
}

/**
 * Report error messages

 * @param response {}
 * @private
 */
function _reportMetricsAndErrors(response) {
  if (response.metric) {
    _metric(response.metric.name, response.metric.value);
  }

  if (response.errorMessage) {
    _displayErrorMessage(response.errorMessage.text, response.errorMessage.details);
  }
}

/**
 * Make an XHR call
 *
 * @param url
 * @param callback
 * @param body
 * @private
 */
function _request(url, callback, body) {
  var req = new XMLHttpRequest();
  req.onreadystatechange = function() {
    if (req.readyState === READY_STATE_DONE && req.status === OK_STATUS) {
      if (req.responseText.trim()) {
        callback(JSON.parse(req.responseText));
      } else {
        callback();
      }
    } else if (req.readyState === READY_STATE_DONE && req.status !== OK_STATUS) {
      _displayErrorMessage(
        i18n.visual_search.something_went_wrong.text,
        i18n.visual_search.something_went_wrong.details);
    }
  };

  _getCloseButton().addEventListener('click', function() {
    _cancelRequest(req);
  });

  req.open('POST', url, true);

  req.setRequestHeader('Content-Type', 'application/json');
  req.setRequestHeader('X-CSRF-Token', _getXsrfToken());
  req.setRequestHeader('X-Requested-With', 'XMLHttpRequest');

  req.send(body);
}

function _getXsrfToken() {
  var metaTags = document.getElementsByTagName('meta');
  for (var i = 0; i < metaTags.length; i++) {
    if (metaTags[i].getAttribute('name') == 'csrf-token') {
      return metaTags[i].getAttribute('content');
    }
  }
}

/**
 * Handles drag enter events on the window.
 *
 * @listens Event
 * @private
 * @param {Event} e
 */
function _handleWindowDragEnter(e) {
  if (!_isFile(e) || !_hasHandleableSource(e)) {
    return;
  }

  _lastTarget = e.target;

  var dropZone = _getDropZone();

  _show(_getDragAndDropUploadForm());
  dropZone.querySelector('.filename').innerText = '';
  dropZone.classList.remove('loading');
  dropZone.classList.add('active');
}

/**
 * Handles drag leave events on the window.
 *
 * @listens Event
 * @private
 * @param {Event} e
 */
function _handleWindowDragLeave(e) {
  e.preventDefault();

  if (e.target === _lastTarget) {
    _hideDragAndDropModal();
  }
}

/**
 * Handles drag over events on the window.
 *
 * @listens Event
 * @private
 * @param {Event} e
 */
function _handleWindowDragOver(e) {
  e.preventDefault();
  e.stopPropagation();
}

function _initDragAndDropForm() {
  document.body.appendChild(_getDragAndDropUploadForm());
}

/**
 * Handles drop events on the window.
 *
 * @listens Event
 * @private
 * @param {Event} e
 */
function _handleWindowDrop(e) {
  if (!_hasHandleableSource(e)) {
    return;
  }
  e.preventDefault();
  _metric('typekit.click.visual-search.drop-image');
  if (e.dataTransfer.files.length == 1) {
    var file = e.dataTransfer.files[0];

    _getDropZone().classList.remove('active');

    window.setTimeout(function() {
      _processAndUploadImage(file);
    }, 1);
  } else if (e.dataTransfer.files.length > 1) {
    _displayErrorMessage(
      i18n.visual_search.uploading_multiple_files.text,
      i18n.visual_search.uploading_multiple_files.details);
  }
}

/**
 * Was the event triggered by a file being dropped onto the page?
 *
 * @private
 * @param {Event} evt
 * @returns {Boolean}
 */
function _isFile(evt) {
  var dt = evt.dataTransfer;

  for (var i = 0; i < dt.types.length; i++) {
    if (dt.types[i] === 'Files') {
      return true;
    }
  }

  return false;
}

function _hasHandleableSource(event) {
  // The "New Library" dialog also has a drop zone, so we have to prevent drop events there from triggering visual search.
  return ((event.srcElement || {}).nodeName || '').toLowerCase() !== 'af-libraries-toolbar';
}

/**
 * Show the element.
 *
 * @private
 * @param {Element} element
 */
function _show(element) {
  element.style.display = 'block';
}

/**
 * Hide the element.
 *
 * @private
 * @param {Element} element
 */
function _hide(element) {
  element.style.display = 'none';
}

/*
 * Display error message in the region where the file was uploaded
 * (either the visual search modal or the "Drop file here to scan for text" box).
 *
 * @private
 * @param {String} text The error message to display
 * @param {String} details Details about the error message
 */
function _displayErrorMessage(text, details) {
  _getDropZone().classList.remove('loading');
  _getDropZone().classList.add('active');

  document.querySelector('.filename').innerText = '';
  _getErrorText().innerHTML = text;
  _getErrorDetails().innerHTML = details;
}

/*
 * Hides the drag and drop modal
 *
 * @private
 *
 * @returns {String}
 */
function _hideDragAndDropModal() {
  _hide(_getDragAndDropUploadForm());
  _resetModalState();

  var dropZone = _getDropZone();
  dropZone.classList.add('loading');
  dropZone.classList.remove('active');
  dropZone.querySelector('.filename').innerText = '';
}

/**
 * Checks if the "Drop file here" box is in the viewport.
 * If so, the drag and drop form is displayed within it.
 * If not, the full screen drag and drop form is displayed.
 *
 * @private
 */
function _toggleDragAndDropUploadFormStyle() {
  if (document.getElementById('drop-file-here-fullscreen') === null) {
    return;
  }

  var rect = document.getElementById(DRAG_AND_DROP_FORM_ID).getBoundingClientRect();
  if (rect.top < 0 || rect.bottom > (window.innerHeight || document.documentElement.clientHeight)) {
    document.getElementById('drop-file-here-fullscreen_frame').classList.add('visual-search-fullscreen__frame');
    document.getElementById('drop-file-here-fullscreen').classList.remove('drop-file-here__drop-zone');
    document.getElementById('drop-file-here').classList.remove('drop-file-here__small');
  } else {
    document.getElementById('drop-file-here-fullscreen_frame').classList.remove('visual-search-fullscreen__frame');
    document.getElementById('drop-file-here-fullscreen').classList.add('drop-file-here__drop-zone');
    document.getElementById('drop-file-here').classList.add('drop-file-here__small');
  }
}

/*
 * Parses an uploaded image's metadata. Used for recording metrics about the upload.
 *
 * @private
 * @param {File} file
 * @param {Function} cb a callback function invoked with the metadata
 */
function _getImageMetadata(file, cb) {
  var _URL = window.URL || window.webkitURL;
  var img = new Image();
  img.onload = function() {
    cb({
      width: this.naturalWidth,
      height: this.naturalHeight,
      size: file.size,
      format: file.type
    });
  };
  img.src = _URL.createObjectURL(file);
}

/*
 * Sends custom metric data to New Relic
 *
 * @private
 * @param {String} name The name of the page action being recorded
 * @param {Object} attributes an object representing the page action's metadata
 */
function _metric(name, attributes) {
  if (window.newrelic && typeof window.newrelic !== 'undefined') {
    window.newrelic.addPageAction(name, attributes || {});
  }
}

/**
 * Process the image file and upload the file.
 *
 * @private
 * @param {File} file
 */
function _processAndUploadImage(file) {
  _resetModalState();
  _showLoadingState(file);

  ImageDownsampler.downsample(file, MAX_FILE_BYTES, ALLOWED_FILE_TYPES, function(base64String, fileName) {
    _getImageMetadata(file, function(imageMetadata) {
      _metric('typekit.click.visual-search.upload-image', imageMetadata);
    });

    _request(_getUploadPath(), function(data) {
      window.location.href = data.path;
    }, JSON.stringify({
      image_string: base64String,
      image_file_name: fileName
    }));
  }, _reportMetricsAndErrors);
}

/**
 * Display the visual search loading state.
 *
 * NOTE: this code is reusing the existing loading state from the drag and drop
 * handler, but should be refactored so the loading state is independent of the
 * drag and drop form.
 *
 * @private
 * @param {File} file
 */
function _showLoadingState(file) {
  _show(_getDragAndDropUploadForm());

  var dropZone = _getDropZone();
  dropZone.classList.add('loading');
  dropZone.querySelector('.filename').innerText = file.name;
}

/*
  Abort the request and remove its state change handlers

  @private
  @param {request} XmlHttpRequest
 */
function _cancelRequest(request) {
  request.onreadystatechange = null;
  request.abort();
}

function _resetModalState() {
  _getErrorText().innerHTML = '';
  _getErrorDetails().innerHTML = '';
}
